import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  CircularProgress,
  Alert
} from '@mui/material';
import { useKeycloak } from '../context/KeycloakContext';
import SystemUpdateButton from '../components/SystemUpdateButton';

const SystemSettings = () => {
  const { keycloak } = useKeycloak();
  const userInfo = keycloak.tokenParsed || {};
  console.log('User Info:', userInfo);
  const isAdmin = userInfo.realm_access?.roles?.includes('system_settings');

  // System settings
  const [systemSettings, setSystemSettings] = useState({
    notificationsEnabled: true,
    autoProcessQueues: true,
    maxQueueRetries: 3,
    logLevel: 'info'
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [saveSuccess, setSaveSuccess] = useState(false);

  // Handle system setting change
  const handleSettingChange = (e) => {
    const { name, value, checked } = e.target;
    setSystemSettings(prev => ({
      ...prev,
      [name]: name.includes('Enabled') || name.includes('auto') ? checked : value
    }));

    // Clear success message when making changes
    if (saveSuccess) setSaveSuccess(false);
  };

  // Save system settings
  const handleSaveSettings = async () => {
    if (!isAdmin) {
      setError('Only administrators can save system settings.');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 800));

      // In a real app, this would be an API call:
      // await systemService.updateSettings(systemSettings);

      setSaveSuccess(true);
    } catch (err) {
      console.error('Error saving system settings:', err);
      setError('Failed to save system settings. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // If not admin, show access denied
  if (!isAdmin) {
    return (
      <Box>
        <Typography variant="h4" gutterBottom>
          System Settings
        </Typography>
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography color="error">
            Access Denied. You need administrator privileges to view system settings.
          </Typography>
        </Paper>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        System Settings
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {saveSuccess && (
        <Alert severity="success" sx={{ mb: 2 }}>
          System settings saved successfully.
        </Alert>
      )}

      {/* System Update Section */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          System Update
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Check for and apply system updates from GitLab releases.
        </Typography>
        <SystemUpdateButton />
      </Paper>

      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          General Settings
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <FormControlLabel
              control={
                <Switch
                  checked={systemSettings.notificationsEnabled}
                  onChange={handleSettingChange}
                  name="notificationsEnabled"
                  color="primary"
                />
              }
              label="Enable Notifications"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControlLabel
              control={
                <Switch
                  checked={systemSettings.autoProcessQueues}
                  onChange={handleSettingChange}
                  name="autoProcessQueues"
                  color="primary"
                />
              }
              label="Auto-Process Queues"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Max Queue Retries"
              type="number"
              name="maxQueueRetries"
              value={systemSettings.maxQueueRetries}
              onChange={handleSettingChange}
              InputProps={{ inputProps: { min: 0, max: 10 } }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>Log Level</InputLabel>
              <Select
                name="logLevel"
                value={systemSettings.logLevel}
                label="Log Level"
                onChange={handleSettingChange}
              >
                <MenuItem value="debug">Debug</MenuItem>
                <MenuItem value="info">Info</MenuItem>
                <MenuItem value="warning">Warning</MenuItem>
                <MenuItem value="error">Error</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleSaveSettings}
                disabled={loading}
              >
                {loading ? <CircularProgress size={24} /> : 'Save Settings'}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
};

export default SystemSettings;
